import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePersistedState, GridView, ContextMenu, LoadingSpinner, ErrorMessage, PendingView, MultiSelectDropdown } from '@code-burrow/design-system';
import { useCollectionData } from '../hooks/useCollectionData.js';
import useAddToList from '../hooks/useAddToList.jsx';
import TableView from './TableView.jsx';
import { applyFilters, applySort } from '../utils/collectionUtils.js';

/**
 * UnifiedCollectionView — a single-page component that renders a collection
 * in either grid or table mode, with filtering, sorting, and persisted state.
 *
 * @param {Object} props
 * @param {import('../types').CollectionAdapter} props.collectionAdapter
 */
export default function UnifiedCollectionView({ collectionAdapter, onItemsLoaded, refreshKey, pendingMode = false, onItemActions }) {
    const {
        storageKey,
        fetchItems,
        filterFields = [],
        sortFields = [],
        renderGridItem,
        renderTableColumns = [],
        emptyState,
        actions = [],
        defaultSortField = 'title',
        defaultSortDirection = 'asc',
        pendingField = null,
        pendingLabel = 'Pending',
        pendingStatuses = [],
        pendingFields = [],
        onPendingSave,
        isPendingItem: customIsPending,
        getImageUrl,
        getTabValue,
        bulkEditFields,
        onBulkSave,
        onBulkDelete,
        getItemId,
        feedRenderer,
        toolbarExtra,
        gridClassName,
        extraFilterFn,
        getCachedItems,
        getContextMenuActions,
        backendService,
        activeView: adapterActiveView,
    } = collectionAdapter;

    // Build default filters from filterFields: { [field.key]: '' }
    const defaultFilters = Object.fromEntries(filterFields.map((f) => [f.key, '']));

    // --- Persisted state ---
    const [viewMode, setViewMode] = usePersistedState(`${storageKey}:viewMode`, 'grid');
    const [filters, setFilters] = usePersistedState(`${storageKey}:filters`, defaultFilters);
    const [rawSortField, setSortField] = usePersistedState(`${storageKey}:sortField`, defaultSortField);
    const [sortDirection, setSortDirection] = usePersistedState(`${storageKey}:sortDirection`, defaultSortDirection);
    // Fall back to default if persisted sort field no longer exists in the options
    const sortField = sortFields.length > 0 && !sortFields.some((f) => f.key === rawSortField) ? defaultSortField : rawSortField;
    const [activeYear, setActiveYear] = usePersistedState(`${storageKey}:activeYear`, String(new Date().getFullYear()));

    // Reset filters and year tab only when the active view changes (not on data refreshes).
    // refreshKey format is "N-viewName", so extract the view portion.
    const getView = (key) => String(key).split('-').slice(1).join('-');
    const prevView = useRef(getView(refreshKey));

    // --- Data layer ---
    const {
        items,
        loading,
        error,
        staleWarning,
        setStaleWarning,
        loadItems,
        resetLoaded,
        updateItemField,
    } = useCollectionData({
        fetchItems,
        getCachedItems,
        getItemId,
        onItemsLoaded,
        onItemActions,
        refreshKey,
    });

    useEffect(() => {
        const currentView = getView(refreshKey);
        if (prevView.current !== currentView) {
            prevView.current = currentView;
            resetLoaded(); // new view → show skeleton again
            setFilters(defaultFilters);
            setActiveYear(String(new Date().getFullYear()));
        }
    }, [refreshKey]);

    // --- Context menu ---
    const [contextMenu, setContextMenu] = useState(null); // { item, position: { x, y } }
    const contextMenuItemRef = useRef(null);
    contextMenuItemRef.current = contextMenu?.item || null;
    const { addToListAction: ctxAddToListAction } = useAddToList({
        backendService: backendService || null,
        activeView: adapterActiveView || '',
        getItemData: useCallback(() => {
            const item = contextMenuItemRef.current;
            return item ? { itemId: item.id, title: item.title || '', imageURL: item.imageURL || item.coverURL || '' } : null;
        }, []),
    });

    const handleContextMenu = useCallback((item, position) => {
        setContextMenu({ item, position });
    }, []);

    const contextMenuActions = contextMenu?.item && getContextMenuActions
        ? [ctxAddToListAction, ...getContextMenuActions(contextMenu.item)]
        : [];

    // --- Filter / sort handlers ---
    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, [setFilters]);

    const handleFilterClear = useCallback(() => {
        setFilters(defaultFilters);
    }, [setFilters, defaultFilters]);

    const handleSort = useCallback((field) => {
        if (field === sortField) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }, [sortField, setSortField, setSortDirection]);

    // --- Partition into main / pending (library) ---
    const hasPending = !!pendingField;
    const pendingStatusSet = new Set(pendingStatuses);
    const isPending = customIsPending || ((i) => {
        if (i[pendingField] == null || i[pendingField] === '') return true;
        if (pendingStatusSet.size > 0 && pendingStatusSet.has(i[pendingField])) return true;
        if (getTabValue && getTabValue(i) == null) return true;
        return false;
    });
    const mainItems = hasPending ? items.filter((i) => !isPending(i)) : items;
    const pendingItems = hasPending ? items.filter(isPending) : [];

    // Use pendingMode prop to select which partition to display
    const sourceItems = hasPending && pendingMode ? pendingItems : mainItems;

    // --- Year tabs (collection mode only) ---
    const hasTabs = !!getTabValue && !pendingMode;
    const yearTabs = React.useMemo(() => {
        if (!hasTabs) return [];
        // getTabValue may return a single year OR an array of years (for rewatched items)
        const allYears = new Set();
        for (const item of mainItems) {
            const val = getTabValue(item);
            if (Array.isArray(val)) {
                for (const y of val) if (y != null) allYears.add(y);
            } else if (val != null) {
                allYears.add(val);
            }
        }
        return [...allYears].sort((a, b) => b - a).map(String);
    }, [hasTabs, mainItems, getTabValue]);

    // Safety net: if persisted year is no longer in the available tabs, reset to current year or latest
    const currentYearStr = String(new Date().getFullYear());
    React.useEffect(() => {
        if (hasTabs && yearTabs.length > 0 && !yearTabs.includes(activeYear)) {
            setActiveYear(yearTabs.includes(currentYearStr) ? currentYearStr : yearTabs[0]);
        }
    }, [hasTabs, yearTabs, activeYear, currentYearStr]);

    // Filter by selected year tab — items appear in every year they were watched.
    // When an item has watchDatesByYear, swap watchedDate to the matching year's date
    // so cards and the detail modal see the correct date for this year tab.
    const tabFilteredItems = hasTabs
        ? sourceItems.filter((item) => {
            const val = getTabValue(item);
            if (Array.isArray(val)) return val.map(String).includes(activeYear);
            return String(val) === activeYear;
        }).map((item) => {
            const yearDate = item.watchDatesByYear?.[activeYear];
            return yearDate ? { ...item, watchedDate: yearDate } : item;
        })
        : sourceItems;

    // --- Derived data ---
    const filteredItems = applyFilters(tabFilteredItems, filters, filterFields);
    const extraFiltered = extraFilterFn ? filteredItems.filter(extraFilterFn) : filteredItems;
    const sortedItems = applySort(extraFiltered, sortField, sortDirection, sortFields);

    const hasActiveFilter = Object.values(filters).some((v) => v !== '');
    const renderFilterField = (field, _filters, _items, onChange) => {
        const value = _filters[field.key] ?? '';
        if (field.type === 'multiselect') {
            const rawOptions = field.getOptions
                ? field.getOptions(_items)
                : (field.staticOptions ?? []);
            const options = rawOptions.map(o => typeof o === 'object' ? o : { value: o, label: o });
            if (options.length === 0) return null;
            // Parse comma-separated string into Set (empty = all selected)
            const selectedSet = value
                ? new Set(value.split(','))
                : new Set(options.map(o => o.value));
            return (
                <MultiSelectDropdown
                    key={field.key}
                    options={options}
                    selected={selectedSet}
                    allLabel={field.allLabel || `All ${field.label}`}
                    onChange={(newSet) => {
                        // Store as comma-separated, or '' if all are selected
                        const allSelected = options.every(o => newSet.has(o.value));
                        const noneSelected = options.every(o => !newSet.has(o.value));
                        onChange(field.key, (allSelected || noneSelected) ? '' : [...newSet].join(','));
                    }}
                />
            );
        }
        if (field.type === 'select') {
            const options = field.getOptions
                ? field.getOptions(_items)
                : (field.staticOptions ?? []);
            if (options.length === 0) return null;
            return (
                <select
                    key={field.key}
                    value={value}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    className="h-9 px-2.5 pr-7 rounded-full bg-surface border border-border-subtle text-text-secondary text-sm font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-primary appearance-none cursor-pointer transition-colors duration-150"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center' }}
                >
                    <option value="">All</option>
                    {options.map((opt) => {
                        const optValue = typeof opt === 'object' ? opt.value : opt;
                        const optLabel = typeof opt === 'object' ? opt.label : opt;
                        return <option key={optValue} value={optValue}>{optLabel}</option>;
                    })}
                </select>
            );
        }
        if (field.type === 'toggle') {
            const active = value === 'true';
            return (
                <button
                    key={field.key}
                    onClick={() => onChange(field.key, active ? '' : 'true')}
                    title={field.label}
                    className={`h-9 px-3 rounded-full border text-sm font-medium transition-colors duration-150 cursor-pointer flex items-center gap-1.5 ${
                        active
                            ? 'bg-accent-primary/15 border-accent-primary text-accent-primary'
                            : 'bg-surface border-border-subtle text-text-secondary hover:text-text-primary'
                    }`}
                >
                    {field.icon || field.label}
                </button>
            );
        }
        return null;
    };

    const isEmptyCollection = tabFilteredItems.length === 0;
    const isNoResults = !isEmptyCollection && filteredItems.length === 0 && hasActiveFilter;

    // --- Render states ---
    if (loading) {
        return <SkeletonGrid />;
    }

    if (error) {
        return (
            <div className="p-4">
                <ErrorMessage
                    message={error}
                    type="api"
                    onDismiss={loadItems}
                />
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={loadItems}
                        className="px-4 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // --- Pending mode: render PendingView instead of the normal collection UI ---
    if (pendingMode && pendingFields.length > 0) {
        return (
            <PendingView
                items={sortedItems}
                pendingFields={pendingFields}
                onSave={onPendingSave}
                onRefresh={loadItems}
                getImageUrl={getImageUrl}
                getItemId={getItemId}
            />
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* Year tabs */}
            {hasTabs && yearTabs.length > 0 && (
                <div className="mb-2">
                    <div className="flex items-start gap-5 overflow-x-auto scrollbar-hide pb-0">
                        {yearTabs.map((year) => (
                            <button
                                key={year}
                                type="button"
                                onClick={() => setActiveYear(year)}
                                className={[
                                    'flex flex-col items-center whitespace-nowrap transition-colors duration-150 flex-shrink-0 font-display',
                                    activeYear === year
                                        ? 'text-4xl text-accent-primary font-black pb-[7px]'
                                        : 'text-2xl text-text-tertiary/40 hover:text-text-tertiary pt-1 font-bold pb-[13px]',
                                ].join(' ')}
                            >
                                {year}
                                {activeYear === year && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-1" />
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="border-b border-border-subtle -mt-[11px]" />
                </div>
            )}

            {/* Toolbar row: sort + filters (desktop) + view toggle */}
            <div className="flex items-center justify-between gap-2 sm:gap-3 flex-nowrap">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    {/* Sort dropdown */}
                    {sortFields.length > 0 && (
                        <SortDropdown
                            sortFields={sortFields}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                            onToggleDirection={() => setSortDirection((d) => d === 'asc' ? 'desc' : 'asc')}
                        />
                    )}
                    {/* Filters — inline with sort on desktop only */}
                    <div className="hidden sm:flex items-center gap-2 sm:gap-3">
                        {filterFields.map((field) => renderFilterField(field, filters, items, handleFilterChange))}
                        {toolbarExtra}
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {actions.length > 0 && (
                        <div className="hidden sm:flex items-center gap-2">
                            {actions.map((action) => (
                                <ActionButton key={action.label} action={action} onRefresh={loadItems} />
                            ))}
                        </div>
                    )}
                    <div className="flex items-center bg-surface rounded-md border border-border-subtle">
                        <button
                            type="button"
                            aria-label="Grid view"
                            aria-pressed={viewMode === 'grid'}
                            onClick={() => setViewMode('grid')}
                            className={[
                                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-l-md text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent-primary',
                                viewMode === 'grid'
                                    ? 'bg-accent-primary text-text-inverse'
                                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                            ].join(' ')}
                        >
                            <GridIcon />
                            <span className="hidden sm:inline">Grid</span>
                        </button>
                        <button
                            type="button"
                            aria-label="Table view"
                            aria-pressed={viewMode === 'table'}
                            onClick={() => setViewMode('table')}
                            className={[
                                `flex items-center gap-1.5 px-2.5 py-1.5 ${feedRenderer ? '' : 'rounded-r-md '}text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent-primary`,
                                viewMode === 'table'
                                    ? 'bg-accent-primary text-text-inverse'
                                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                            ].join(' ')}
                        >
                            <TableIcon />
                            <span className="hidden sm:inline">Table</span>
                        </button>
                        {feedRenderer && (
                            <button
                                type="button"
                                aria-label="Feed view"
                                aria-pressed={viewMode === 'feed'}
                                onClick={() => setViewMode('feed')}
                                className={[
                                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-r-md text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent-primary',
                                    viewMode === 'feed'
                                        ? 'bg-accent-primary text-text-inverse'
                                        : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                                ].join(' ')}
                            >
                                <FeedIcon />
                                <span className="hidden sm:inline">Feed</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Filters — separate row on mobile */}
            {(filterFields.length > 0 || toolbarExtra) && (
                <div className="flex sm:hidden items-center gap-2 flex-wrap">
                    {filterFields.map((field) => renderFilterField(field, filters, items, handleFilterChange))}
                    {toolbarExtra}
                </div>
            )}

            {/* Item count */}
            {!isEmptyCollection && (
                <p className="text-sm text-text-secondary">
                    {sortedItems.length}{sortedItems.length !== tabFilteredItems.length ? ` of ${tabFilteredItems.length}` : ''} {sortedItems.length === 1 ? 'item' : 'items'}
                </p>
            )}

            {/* Stale data warning */}
            {staleWarning && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-400">
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="flex-1">{staleWarning}</span>
                    <button onClick={loadItems} className="font-medium underline underline-offset-2 hover:text-amber-300">Retry</button>
                    <button onClick={() => setStaleWarning(null)} className="opacity-60 hover:opacity-100 ml-1">&times;</button>
                </div>
            )}

            {/* Content area */}
            {isEmptyCollection ? (
                <EmptyState emptyState={emptyState} />
            ) : isNoResults ? (
                <NoResults onClear={handleFilterClear} />
            ) : viewMode === 'feed' && feedRenderer ? (
                feedRenderer()
            ) : viewMode === 'grid' ? (
                <GridView items={sortedItems} renderGridItem={renderGridItem} className={gridClassName} onItemLongPress={getContextMenuActions ? handleContextMenu : undefined} />
            ) : (
                <TableView
                    items={sortedItems}
                    columns={renderTableColumns}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    bulkEditFields={bulkEditFields}
                    onBulkSave={async (selectedItems, updates) => {
                        try {
                            await onBulkSave?.(selectedItems, updates);
                        } finally {
                            loadItems();
                        }
                    }}
                    onBulkDelete={onBulkDelete ? async (selectedItems) => {
                        try {
                            await onBulkDelete(selectedItems);
                        } finally {
                            loadItems();
                        }
                    } : undefined}
                    getItemId={getItemId}
                    onItemUpdated={updateItemField}
                    onRowLongPress={getContextMenuActions ? handleContextMenu : undefined}
                />
            )}

            {/* Context menu (long-press / right-click on cards/rows) */}
            {contextMenu && contextMenuActions.length > 0 && (
                <ContextMenu
                    position={contextMenu.position}
                    actions={contextMenuActions}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </div>
    );
}

function ActionButton({ action, onRefresh }) {
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState(null);

    const handleClick = async () => {
        setLoading(true);
        setResult(null);
        try {
            const res = await action.onClick();
            setResult({ success: true, message: res?.message ?? 'Done' });
            onRefresh();
        } catch (err) {
            setResult({ success: false, message: err?.message ?? 'Failed' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={handleClick}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <RefreshIcon spinning={loading} />
                {loading ? 'Refreshing…' : action.label}
            </button>
            {result && (
                <span className={`absolute top-full mt-1 right-0 text-xs whitespace-nowrap px-2 py-1 rounded shadow-md z-10 ${result.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                    {result.message}
                </span>
            )}
        </div>
    );
}

function EmptyState({ emptyState }) {
    if (!emptyState) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-text-secondary text-lg">No items yet.</p>
            </div>
        );
    }
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <h3 className="text-text-primary text-xl font-semibold">{emptyState.title}</h3>
            {emptyState.description && (
                <p className="text-text-secondary max-w-md">{emptyState.description}</p>
            )}
            {emptyState.action && <div className="mt-2">{emptyState.action}</div>}
        </div>
    );
}

function NoResults({ onClear }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <p className="text-text-secondary text-lg">No results match your filters.</p>
            <button
                type="button"
                onClick={onClear}
                className="px-4 py-2 rounded-md border border-border text-text-secondary text-sm font-medium hover:bg-surface-hover hover:text-text-primary transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent-primary"
            >
                Clear Filters
            </button>
        </div>
    );
}

function GridIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" />
            <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" />
            <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" />
            <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" />
        </svg>
    );
}

function TableIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="14" height="3" rx="1" fill="currentColor" />
            <rect x="1" y="6" width="14" height="3" rx="1" fill="currentColor" opacity="0.7" />
            <rect x="1" y="11" width="14" height="3" rx="1" fill="currentColor" opacity="0.5" />
        </svg>
    );
}

function FeedIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="3" height="3" rx="1.5" fill="currentColor" />
            <rect x="6" y="1.5" width="9" height="2" rx="1" fill="currentColor" />
            <rect x="1" y="6.5" width="3" height="3" rx="1.5" fill="currentColor" />
            <rect x="6" y="7" width="9" height="2" rx="1" fill="currentColor" />
            <rect x="1" y="12" width="3" height="3" rx="1.5" fill="currentColor" />
            <rect x="6" y="12.5" width="9" height="2" rx="1" fill="currentColor" />
        </svg>
    );
}

function SortDropdown({ sortFields, sortField, sortDirection, onSort, onToggleDirection }) {
    const currentLabel = sortFields.find((f) => f.key === sortField)?.label ?? sortField;

    return (
        <div className="flex items-center gap-1.5">
            <label className="text-xs text-text-tertiary font-medium uppercase tracking-wide sr-only">Sort</label>
            <select
                value={sortField}
                onChange={(e) => onSort(e.target.value)}
                className="bg-surface border border-border-subtle rounded-md px-2.5 py-1.5 text-sm text-text-secondary font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-primary appearance-none cursor-pointer pr-7"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center' }}
            >
                {sortFields.map((f) => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                ))}
            </select>
            <button
                type="button"
                onClick={onToggleDirection}
                aria-label={`Sort ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
                className="flex items-center justify-center h-8 w-8 bg-surface rounded-md border border-border-subtle text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-primary"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                    style={sortDirection === 'desc' ? { transform: 'rotate(180deg)' } : undefined}
                >
                    <path d="M12 5v14M5 12l7-7 7 7" />
                </svg>
            </button>
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div className="flex flex-col gap-4">
            {/* Skeleton toolbar */}
            <div className="flex items-center justify-between">
                <div className="h-9 w-32 bg-surface rounded-md animate-pulse" />
                <div className="h-9 w-24 bg-surface rounded-md animate-pulse" />
            </div>
            {/* Skeleton cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="bg-surface rounded-lg overflow-hidden">
                        <div className="pb-[56.25%] bg-bg-tertiary animate-pulse" />
                        <div className="p-4 space-y-2">
                            <div className="h-4 bg-bg-tertiary rounded animate-pulse w-3/4" />
                            <div className="h-3 bg-bg-tertiary rounded animate-pulse w-1/2" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function RefreshIcon({ spinning }) {
    return (
        <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            style={spinning ? { animation: 'spin 1s linear infinite' } : undefined}
        >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
    );
}
